import {
  CHAT_MESSAGE_MAX,
  ChatItem,
  QuestionDefinition,
  type ChatItemDraft,
  type ChatItemInput,
} from '@cyberscore/contracts';
import { RotateCcw } from 'lucide-react';
import { useEffect, useReducer, useRef, useState } from 'react';
import { useToast } from '../../ui/Toasts';
import { ChatThread, Composer, Typing, type ChatHandlers } from './ChatThread';
import { DEMO_QUESTIONS, demoReducer, startDemo, type DemoAction } from './demo';

/**
 * Chat building blocks (task 16), for design review. The real screen is ChatScreen (task 28), on the
 * conversation API (task 25). Scripted: nothing here calls a model or saves an answer.
 */
export function ChatGallery() {
  return (
    <>
      <div className="page-head">
        <h1>Chat design</h1>
        <p>
          The pieces of the assessment chat: quick replies, confirmation cards and form cards. Scripted, so nothing
          here uses AI or saves an answer. The rules are written up in docs/design/chat-ux.md.
        </p>
      </div>
      <TryIt />
      <h2 className="section-title">Every state</h2>
      <States />
    </>
  ); // prettier-ignore
}

const SUGGESTIONS = [
  'We use Outlook and Teams on Microsoft 365, about 96 people',
  'Why do you need to know this?',
  'Not sure, my IT guy set it up',
];

function TryIt() {
  const [state, dispatch] = useReducer(demoReducer, undefined, startDemo);
  const [busy, setBusy] = useState<string | null>(null);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  // Typed replies take a moment, as the agent will; taps are instant.
  const later = (action: DemoAction, label: string) => {
    setBusy(label);
    timer.current = window.setTimeout(() => {
      setBusy(null);
      dispatch(action);
    }, 700);
  };
  const handlers: ChatHandlers = {
    answer: (question, value, text, via) => dispatch({ type: 'answer', question, value, text, via }),
    confirm: (card, questions) => dispatch({ type: 'confirm', card, questions }),
    decline: (card, questions) => dispatch({ type: 'decline', card, questions }),
    skip: (question) => dispatch({ type: 'skip', question }),
  };

  return (
    <div className="chat-layout">
      <section className="card chat" aria-label="Try the chat">
        <div className="chat-h">
          <div className="grow">
            <b>Cyber check</b>
            <span>Scripted example · Kinta Precision (fictional)</span>
          </div>
          <button type="button" className="btn btn-sm btn-ghost" onClick={() => dispatch({ type: 'reset' })}>
            <RotateCcw size={14} /> Start again
          </button>
        </div>
        <ChatThread items={state.items} questions={DEMO_QUESTIONS} handlers={handlers} busy={busy} />
        <div className="chat-try" aria-label="Example replies">
          <span>Try</span>
          {SUGGESTIONS.map((s) => (
            <button key={s} type="button" className="chip" disabled={!!busy} onClick={() => later({ type: 'send', text: s }, 'Reading your reply')}>
              “{s}”
            </button>
          ))}
        </div>
        <Composer maxLength={CHAT_MESSAGE_MAX} disabled={!!busy} onSend={(text) => later({ type: 'send', text }, 'Reading your reply')} />
      </section>

      <aside className="card pad rules">
        <div className="eyebrow">How it behaves</div>
        <ul>
          <li><b>Tap to answer.</b> A quick reply or form card is your own answer and is saved at once.</li>
          <li><b>Typed replies are checked first.</b> What the assistant understood comes back on a card; nothing is saved until you confirm.</li>
          <li><b>One reply can answer several questions.</b> They arrive on one card.</li>
          <li><b>Answers that matter most are confirmed one by one:</b> required questions and those that can cap the score.</li>
          <li><b>Moving on expires a card.</b> Typing again or tapping an answer leaves older proposals unsaved.</li>
          <li><b>Long lists, tables and dates</b> open a small form inside the chat.</li>
        </ul>
      </aside>
    </div>
  ); // prettier-ignore
}

// ---------- static examples ----------

const LONG_LIST = QuestionDefinition.parse({
  type: 'single_choice',
  text: 'Which state is your main site in?',
  options: ['Johor', 'Kedah', 'Kelantan', 'Melaka', 'Negeri Sembilan', 'Pahang', 'Perak', 'Perlis', 'Pulau Pinang', 'Sabah', 'Sarawak', 'Selangor', 'Terengganu', 'Kuala Lumpur'].map((l) => ({ value: l.toLowerCase().replace(/ /g, '_'), label: l })),
}); // prettier-ignore
const QUESTIONS = { ...DEMO_QUESTIONS, STATE: LONG_LIST };

let n = 0;
const items = (...list: ChatItemDraft[]) => list.map((it) => ChatItem.parse({ ...it, id: `x${++n}` }));
const ask = (question: string, text: string, extra: Partial<Extract<ChatItemInput, { kind: 'question' }>> = {}) => ({
  kind: 'question' as const,
  question,
  text,
  presentation: 'quick_replies' as const,
  ...extra,
}); // prettier-ignore
const reply = (o: { value: string; label: string }[]) => o.map(({ value, label }) => ({ value, label }));
const row = (question: string, label: string, value: unknown, own: boolean, extra: object = {}) => ({
  question,
  question_text: (QUESTIONS as Record<string, QuestionDefinition>)[question]?.text ?? 'When did you last restore files from a backup to check it works?',
  value,
  label,
  confidence: 0.9,
  quote: null,
  own_confirmation: own,
  ...extra,
}); // prettier-ignore

function examples() {
  const q = DEMO_QUESTIONS;
  return [
    {
      title: 'Quick replies',
      note: 'Choices of up to eight options. One tap saves the answer.',
      items: items(ask('Q021', 'Which email system does the team use?', { why: 'It decides which connections we can offer you.', quick_replies: reply(q.Q021!.options) })), // prettier-ignore
    },
    {
      title: 'Answer that can cap the score',
      note: 'Kill-switch questions say so before the person answers.',
      items: items(ask('Q058', 'When people sign in to email, do they also need a code or a prompt on their phone?', { kill_switch: true, quick_replies: reply(q.Q058!.options) })), // prettier-ignore
    },
    {
      title: 'Pick several',
      note: 'Chips toggle; Done sends them, in the configured order.',
      items: items(ask('Q330', 'Which systems would stop production or sales if they were down for a day?', { multiple: true, quick_replies: reply(q.Q330!.options) })), // prettier-ignore
    },
    {
      title: 'Typed answer',
      note: 'Numbers and short text are typed; a form is there for anyone who prefers it.',
      items: items(
        ask('Q012', 'Roughly how many people work for you, including part-timers?', {
          presentation: 'typed',
        }),
      ),
    },
    {
      title: 'Inline form card',
      note: 'Long lists, tables, locations and dates open a small form inside the chat.',
      items: items(ask('STATE', 'Which state is your main site in?', { presentation: 'inline_form' })),
    },
    {
      title: 'Date, optional',
      note: 'Optional questions can be skipped.',
      items: items(
        ask('Q106', 'When does the certificate expire?', { presentation: 'date_picker', optional: true }),
      ),
    },
    {
      title: 'Upload',
      note: 'Shown, but not available until document intelligence arrives.',
      items: items(ask('Q402', 'Do you have your insurance policy schedule to hand?', { presentation: 'upload', optional: true })), // prettier-ignore
    },
    {
      title: 'Confirmation: one answer',
      note: 'What the assistant read from a typed reply, with the words it relied on.',
      items: items(
        { kind: 'user', text: 'We use Outlook and Teams', via: 'typed' },
        { kind: 'confirmation', turn_id: 't1', source: { kind: 'chat' }, rows: [row('Q021', 'Microsoft 365', 'microsoft_365', true, { confidence: 0.92, quote: 'We use Outlook and Teams' })] }, // prettier-ignore
      ),
    },
    {
      title: 'Confirmation: several answers',
      note: 'Required answers get their own Confirm; the rest are confirmed together.',
      items: items(
        {
          kind: 'user',
          text: 'Microsoft 365, about 96 people, and no, we are not ISO certified',
          via: 'typed',
        },
        { kind: 'confirmation', turn_id: 't2', source: { kind: 'chat' }, rows: [
          row('Q021', 'Microsoft 365', 'microsoft_365', true, { quote: 'Microsoft 365' }),
          row('Q012', '96 people', 96, true, { quote: 'about 96 people', confidence: 0.82 }),
          row('Q104', 'No', 'no', false, { quote: 'we are not ISO certified', confidence: 0.86 }),
        ] }, // prettier-ignore
      ),
    },
    {
      title: 'Confirmation: from a document',
      note: 'Cites the page. Includes a kill-switch answer, so it says nothing is saved until confirmed.',
      items: items({ kind: 'confirmation', turn_id: 't3', source: { kind: 'document', document_id: 'd1', name: 'BCP_2025_v3.pdf' }, rows: [
        row('Q283', '3–12 months ago', '3_12_months', true, { page: 14, quote: 'Last full restore test: 14 Nov 2024.', kill_switch: true, confidence: 0.91 }),
      ] }), // prettier-ignore
    },
    {
      title: 'After confirming',
      note: 'Each row keeps its outcome, so the transcript stays an honest record.',
      items: items({ kind: 'confirmation', turn_id: 't4', source: { kind: 'chat' }, rows: [
        row('Q021', 'Microsoft 365', 'microsoft_365', true, { state: 'confirmed' }),
        row('Q012', '96 people', 96, true, { state: 'declined' }),
        row('Q104', 'No', 'no', false, { state: 'expired' }),
      ] }), // prettier-ignore
    },
    {
      title: 'Explanation',
      note: 'A question about a term or the score, answered by the explainer from the control library, with what it drew on.',
      items: items(
        { kind: 'user', text: 'what does an offline backup copy mean?', via: 'typed' },
        {
          kind: 'bot',
          text: 'It’s a copy of your files that ransomware can’t reach or change, like a spare key kept at home. If attackers lock your systems, you restore from it instead of paying.',
          sources: [
            {
              id: 'glossary:immutable-backup',
              title: "A backup attackers can't change (immutable or offline)",
            },
            { id: 'control:BR-02', title: 'Immutable or offline backup copy' },
          ],
        },
      ),
    },
    {
      title: 'Feedback and milestones',
      note: 'Short feedback after saving; stage and finish messages from the journey.',
      items: items(
        { kind: 'bot', text: 'Saved. MFA for everyone closes the most common way in.', score_delta: 38 },
        { kind: 'stage', stage: 'S01', name: 'Know your business', message: 'Nice. We now know enough to tailor the rest of the check.' }, // prettier-ignore
        { kind: 'done', answered: 12, required_left: 0 },
      ),
      typing: true,
    },
  ];
}

const EXAMPLES = examples();

function States() {
  const toast = useToast();
  const handlers: ChatHandlers = {
    answer: (_q, _v, text) => toast(`Example only: would save “${text}”. Try it in the conversation above.`),
    confirm: () => toast('Example only: would save the answer. Try it in the conversation above.'),
    decline: () => toast('Example only: would ask the question again.'),
    skip: () => toast('Example only: would skip this question.'),
  };
  return (
    <div className="state-grid">
      {EXAMPLES.map((ex) => (
        <section key={ex.title} className="card state">
          <div className="state-h">
            <b>{ex.title}</b>
            <span className="muted small">{ex.note}</span>
          </div>
          <ChatThread items={ex.items} questions={QUESTIONS} handlers={handlers} scroll={false} />
          {ex.typing && (
            <div className="chat-log tail">
              <Typing label="Reading your reply" />
            </div>
          )}
        </section>
      ))}
    </div>
  );
}
